import { Fragment, type ReactNode, useRef, useState } from "react";
import { useLocation } from "react-router";
import { usePhone } from "../../../shared/phone/media";
import { useEdgeSwipeBack, usePhoneViewport, usePullToRefresh, useScrollMemory } from "../../../shared/phone/shell";
import { Rail } from "./Rail";
import { isTabRoot, TabBar } from "./TabBar";
import { Toaster } from "./Toast";

/**
 * The portal's frame on a phone and an iPad: the screen scrolls inside `.pn-main`, so the bars stay put and
 * the keyboard never covers them. With `nav`, the tabs along the bottom of a phone (on each tab's own
 * screen) or the rail down an iPad, and a pull at the top reloads the screen; without, a screen on its own
 * (the programs list before one is picked, and setting up).
 */
export function NativeFrame({ nav, onRefresh, children }: { nav: boolean; onRefresh?: () => void; children: ReactNode }) {
  usePhoneViewport();
  useEdgeSwipeBack();
  const phone = usePhone();
  const main = useRef<HTMLElement>(null);
  useScrollMemory(main);
  const { pathname } = useLocation();
  // A pull to refresh draws the screen again from scratch, so it loads afresh.
  const [epoch, setEpoch] = useState(0);
  const pullRing = usePullToRefresh(() => {
    if (!nav) return;
    setEpoch((n) => n + 1);
    onRefresh?.();
  });

  const base = phone ? "pn-app pn-ph" : "pn-app pn-ip";
  const cls = !nav ? `${base} po-bare` : phone && isTabRoot(pathname) ? `${base} pn-tabbed` : base;
  return (
    <div className={cls}>
      {nav ? <Rail /> : null}
      <main className="pn-main" ref={main}>
        <Fragment key={epoch}>{children}</Fragment>
      </main>
      {nav ? <TabBar /> : null}
      {nav ? (
        <div className="pn-pull" ref={pullRing} aria-hidden="true">
          <svg viewBox="0 0 24 24">
            <path d="M20 12a8 8 0 1 1-2.34-5.66M20 4v5h-5" />
          </svg>
        </div>
      ) : null}
      <Toaster />
    </div>
  );
}
